/**
 * 카테고리 메인 v2 API — keys(편성 키) / list, list/pc(네비게이션) / 구좌 목록·디테일
 */
import { get, post } from '@apis/api';
import type { CategoryDesktop } from '@models/category';
import type { PageCommonResponse } from '@models/common';
import type {
  CornerArea,
  CornerAreaDisplayContentKey,
  CornerAreaListData,
} from '@models/cornerArea';

const baseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;
const options = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    'DISPLAY-API-KEY': process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_KEY || '',
  },
};

const BASE_PATH = '/display/api/category/v2';

/** 어드민 미리보기(displayDate)면 /admin 경로 */
const withAdmin = (url: string, displayDate?: string): string =>
  displayDate ? `${url}/admin` : url;

export const CATEGORY_MAIN_PAGE_SIZE_MAX = 10;

export interface CategoryMainPageKey {
  mediumCd: string;
  categoryCd: string | null;
}

// 구좌 경로 키 = keys 의 mappingKey 규칙 (`${mediumCd}_${categoryCd}` 또는 mediumCd)
export const buildKeyPath = ({
  mediumCd,
  categoryCd,
}: CategoryMainPageKey): string =>
  categoryCd ? `${mediumCd}_${categoryCd}` : mediumCd;

export interface CategoryMainAreaListParam {
  pageNo: number;
  pageSize: number;
  recommendItems?: string[];
  /** 어드민 미리보기 기준일(YYYY-MM-DD) — URL ?previewDate= */
  displayDate?: string;
}

export interface CategoryMainAreaDetailParam {
  recommendItems?: string[];
  tabCode?: string;
  displayDate?: string;
  /** 몰로코 광고 구좌(PD010/BP007) 전용 */
  adRequestId?: string;
  adSessionId?: string;
  adPageId?: string;
  /** BP002 전용 : 이 키들의 컨텐츠만 상세로 받는다 */
  displayContentKeys?: CornerAreaDisplayContentKey[];
}

interface CategoryMainAreaListResponse extends PageCommonResponse {
  data: CornerAreaListData;
}

interface CategoryMainAreaDetailResponse extends PageCommonResponse {
  data: CornerArea;
}

export interface CategoryMainKeyInfo {
  categoryType: string;
  categoryCd: string | null;
  title: string;
  mappingKey: string;
}

interface CategoryMainKeysResponse extends PageCommonResponse {
  data: { keys: CategoryMainKeyInfo[] };
}

export const requestCategoryMainKeys = async (): Promise<
  CategoryMainKeyInfo[]
> => {
  const { data } = await get<CategoryMainKeysResponse>({
    url: `${BASE_PATH}/keys`,
    options,
    baseUrl: baseURL,
  });

  return data?.keys ?? [];
};

export interface CategoryNavigationParam {
  categoryType: string;
  category: string;
  gender?: string;
  shopcd?: string;
}

const buildNavigationQuery = ({
  categoryType,
  category,
  gender,
  shopcd,
}: CategoryNavigationParam): string => {
  const query = new URLSearchParams({ categoryType, category });
  if (gender) {
    query.set('gender', gender);
  }
  if (shopcd) {
    query.set('shopcd', shopcd);
  }
  return query.toString();
};

export interface CategoryNavigationItem {
  mediumCd: string;
  categoryName: string;
  categoryEnName?: string;
  categoryKrName?: string;
  categoryCd?: number;
  largeCategory: string;
  middleCategory: string;
  smallCategory: string;
  displayOrder: number;
  landingUrl?: string | null;
  subCategory?: CategoryNavigationItem[] | null;
}

export interface CategoryNavigationData {
  categoryPath: string[];
  shortcutCategory: CategoryNavigationItem[];
  subCategory: CategoryNavigationItem[];
  leafCategory?: CategoryNavigationItem[] | null;
  frontShopParameter?: string | null;
}

interface CategoryNavigationResponse extends PageCommonResponse {
  data: CategoryNavigationData;
}

interface CategoryNavigationPcResponse extends PageCommonResponse {
  data: CategoryDesktop;
}

export const requestCategoryNavigation = async (
  param: CategoryNavigationParam
): Promise<CategoryNavigationData> => {
  const { data } = await get<CategoryNavigationResponse>({
    url: `${BASE_PATH}/list?${buildNavigationQuery(param)}`,
    options,
    baseUrl: baseURL,
  });

  return data;
};

export const requestCategoryNavigationPc = async (
  param: CategoryNavigationParam
): Promise<CategoryDesktop> => {
  const { data } = await get<CategoryNavigationPcResponse>({
    url: `${BASE_PATH}/list/pc?${buildNavigationQuery(param)}`,
    options,
    baseUrl: baseURL,
  });

  return data;
};

export const requestCategoryMainAreaList = async (
  key: CategoryMainPageKey,
  param: CategoryMainAreaListParam
): Promise<CornerAreaListData> => {
  const { data } = await post<CategoryMainAreaListResponse>({
    url: withAdmin(`${BASE_PATH}/${buildKeyPath(key)}`, param.displayDate),
    data: param,
    options,
    baseUrl: baseURL,
  });

  return data;
};

export const requestCategoryMainAreaDetail = async (
  key: CategoryMainPageKey,
  areaSubType: string,
  areaId: number,
  param: CategoryMainAreaDetailParam = {}
): Promise<CornerArea> => {
  const { data } = await post<CategoryMainAreaDetailResponse>({
    url: withAdmin(
      `${BASE_PATH}/${buildKeyPath(key)}/${areaSubType}/${areaId}`,
      param.displayDate
    ),
    data: param,
    options,
    baseUrl: baseURL,
  });

  return data;
};
